import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Download, Lock } from "lucide-react";
import { api, fileUrl, euro } from "../lib/api";
import { Layout } from "../components/Layout";
import { BiniBubble } from "../components/Bini";
import { HiddenStar } from "../components/HiddenStar";
import { OfferBadge, OfferNote } from "../components/OfferBadge";
import { t } from "../i18n";

export const ColoringCard = ({ c, index = 0 }) => (
  <div className="cloud-card overflow-hidden !rounded-[32px] anim-pop" style={{ animationDelay: `${index * 60}ms` }} data-testid={`coloring-card-${c.id}`}>
    <div className="aspect-[3/4] bg-white"><img src={fileUrl(`/files/colorings/${c.id}/preview`)} alt="" loading="lazy" className="h-full w-full object-contain p-3" /></div>
    <div className="p-4">
      <h3 className="text-lg font-bold leading-tight">{c.title}</h3>
      <a href={fileUrl(`/files/colorings/${c.id}/pdf`)} download data-testid={`coloring-download-${c.id}`}
        className="btn-3d no-shine mt-3 w-full !min-h-[56px] !text-base !px-3 !gap-1.5" style={{ "--c": "#4CC417" }}>
        <Download className="w-5 h-5 shrink-0" strokeWidth={2.5} /> {t("coloring.download")}
      </a>
    </div>
  </div>
);

const PackCard = ({ p }) => (
  <div className="cloud-card overflow-hidden !rounded-[32px]" data-testid={`pack-card-${p.id}`}>
    <div className="relative aspect-[4/3] bg-white">
      <img src={fileUrl(`/files/packs/${p.id}/preview`)} alt="" loading="lazy" className="h-full w-full object-contain p-3" />
      <span className="absolute right-3 top-3 rounded-full bg-[#8A3FE0] px-3 py-1 text-sm font-bold text-white">{euro(p.price)}</span>
      {p.offer && <OfferBadge offer={p.offer} testId={`pack-offer-badge-${p.id}`} className="absolute left-3 top-3" />}
    </div>
    <div className="p-4">
      <h3 className="text-lg font-bold leading-tight">{p.title}</h3>
      {p.description && <p className="mt-1 text-sm opacity-80" data-testid={`pack-description-${p.id}`}>{p.description}</p>}
      {p.offer && <OfferNote offer={p.offer} testId={`pack-offer-note-${p.id}`} />}
      <Link to="/espace-parents" data-testid={`pack-ask-adult-${p.id}`} className="btn-3d mt-3 w-full !min-h-[56px] !text-lg" style={{ "--c": "#8A3FE0" }}>
        <Lock className="w-5 h-5" strokeWidth={2.5} /> {t("coloring.askAdult")}
      </Link>
    </div>
  </div>
);

export default function Coloring() {
  const [themes, setThemes] = useState([]);
  const [theme, setTheme] = useState("");
  const [items, setItems] = useState(null);
  const [packs, setPacks] = useState([]);

  useEffect(() => { api.get("/themes").then((r) => setThemes(r.data)); }, []);
  useEffect(() => {
    const params = theme ? { theme } : {};
    api.get("/colorings", { params }).then((r) => setItems(r.data));
    api.get("/packs", { params }).then((r) => setPacks(r.data));
  }, [theme]);

  return (
    <Layout back="/">
      <div className="relative">
        <h1 className="text-4xl sm:text-5xl font-extrabold text-white [text-shadow:0_3px_0_rgba(30,42,90,0.45),0_0_14px_rgba(30,42,90,0.45)]">{t("coloring.title")}</h1>
        <HiddenStar id="coloring" className="right-2 -top-1" />
      </div>
      <div className="mt-6 flex gap-3 overflow-x-auto no-scrollbar py-2 -mx-4 px-4">
        {[{ slug: "", name: t("coloring.all"), color: "#FF8A1F" }, ...themes].map((th) => (
          <button key={th.slug || "all"} type="button" onClick={() => setTheme(th.slug)} aria-pressed={theme === th.slug}
            data-testid={`coloring-theme-${th.slug || "all"}`}
            className={`btn-3d !min-h-[56px] !text-lg !px-5 shrink-0 ${theme === th.slug ? "" : "is-off"}`} style={{ "--c": th.color }}>{th.name}</button>
        ))}
      </div>
      {items && items.length === 0 && packs.length === 0 && <BiniBubble testId="coloring-empty-state" className="mt-10">{t("coloring.empty")}</BiniBubble>}
      <div className="mt-8 grid grid-cols-2 gap-4 sm:gap-6 lg:grid-cols-4" data-testid="coloring-grid">
        {items?.map((c, i) => <ColoringCard key={c.id} c={c} index={i} />)}
      </div>
      {packs.length > 0 && (
        <section className="mt-12" data-testid="coloring-packs">
          <h2 className="text-2xl sm:text-3xl font-extrabold text-white [text-shadow:0_3px_0_rgba(30,42,90,0.45)]">{t("coloring.packs")}</h2>
          <p data-testid="coloring-packs-hint" className="mt-1 font-semibold text-white [text-shadow:0_1px_4px_rgba(30,42,90,0.8)]">{t("coloring.packsHint")}</p>
          <div className="mt-5 grid gap-4 sm:gap-6 sm:grid-cols-2 lg:grid-cols-3">{packs.map((p) => <PackCard key={p.id} p={p} />)}</div>
        </section>
      )}
    </Layout>
  );
}
